import React from 'react';
import { Box, BoxProps } from '../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { LegendKey } from '../../../../CommonComponents/OPL/charts/LegendKey/LegendKey';

interface Props {
  isRsu: boolean;
  totalShares: number;
  alreadyVestedShares: number;
  yetToVestShares: number;
  ownedSharesCount: number;
  rsuVestedAndOwned: number;
  sharesToAllocate: number;
  /** Horizontal padding for the copy and the progress bar. */
  px?: BoxProps['px'];
}

const Emphasis = ({ children }: { children: React.ReactNode }) => (
  <Text as="span" fontWeight={600} color="neutral.500">
    {children}
  </Text>
);

const toPercentWidth = (value: number, total: number) => `${(value / (total || 1)) * 100}%`;

export const VestingProgressSummary = ({
  isRsu,
  totalShares,
  alreadyVestedShares,
  yetToVestShares,
  ownedSharesCount,
  rsuVestedAndOwned,
  sharesToAllocate,
  px = '24',
}: Props) => {
  return (
    <>
      <Box px={px} pb="16">
        <Text color="neutral.500">
          {isRsu ? (
            <>
              Of the <Emphasis>{totalShares.toLocaleString()}</Emphasis> shares in this grant,{' '}
              <Emphasis>{rsuVestedAndOwned.toLocaleString()}</Emphasis> are vested and owned, and{' '}
              <Emphasis>{yetToVestShares.toLocaleString()}</Emphasis> are scheduled to vest.
            </>
          ) : (
            <>
              Of the <Emphasis>{totalShares.toLocaleString()}</Emphasis> shares in this grant,{' '}
              <Emphasis>{(alreadyVestedShares + ownedSharesCount).toLocaleString()}</Emphasis> are
              vested, and <Emphasis>{yetToVestShares.toLocaleString()}</Emphasis> are scheduled to
              vest.
              {ownedSharesCount > 0 && alreadyVestedShares > 0 && (
                <>
                  {' '}
                  Of the{' '}
                  <Emphasis>
                    {(alreadyVestedShares + ownedSharesCount).toLocaleString()}
                  </Emphasis>{' '}
                  shares that are vested, <Emphasis>{ownedSharesCount.toLocaleString()}</Emphasis>{' '}
                  are already exercised and owned, and{' '}
                  <Emphasis>{alreadyVestedShares.toLocaleString()}</Emphasis> have yet to be
                  exercised.
                </>
              )}
            </>
          )}
        </Text>
      </Box>

      {totalShares > 0 && (
        <Box px={px} pb="16">
          <Box display="flex" height="8px" borderRadius="small" overflow="hidden" bg="neutral.100">
            {isRsu ? (
              <>
                {rsuVestedAndOwned > 0 && (
                  <Box bg="success.500" width={toPercentWidth(rsuVestedAndOwned, totalShares)} />
                )}
                {yetToVestShares > 0 && (
                  <Box bg="info.400" width={toPercentWidth(yetToVestShares, totalShares)} />
                )}
              </>
            ) : (
              <>
                {alreadyVestedShares > 0 && (
                  <Box bg="success.500" width={toPercentWidth(alreadyVestedShares, totalShares)} />
                )}
                {yetToVestShares > 0 && (
                  <Box bg="info.400" width={toPercentWidth(yetToVestShares, totalShares)} />
                )}
                {ownedSharesCount > 0 && (
                  <Box bg="purple.500" width={toPercentWidth(ownedSharesCount, totalShares)} />
                )}
              </>
            )}
            {sharesToAllocate > 0 && (
              <Box bg="neutral.200" width={toPercentWidth(sharesToAllocate, totalShares)} />
            )}
          </Box>
          <Box display="flex" mt="8" gridGap="16" flexWrap="wrap">
            {isRsu ? (
              <>
                <LegendKey
                  dotColor="success.500"
                  text={`Vested and owned: ${rsuVestedAndOwned.toLocaleString()}`}
                />
                <LegendKey
                  dotColor="info.400"
                  text={`Scheduled to vest: ${yetToVestShares.toLocaleString()}`}
                />
              </>
            ) : (
              <>
                {alreadyVestedShares > 0 && (
                  <LegendKey
                    dotColor="success.500"
                    text={`Vested (not yet exercised): ${alreadyVestedShares.toLocaleString()}`}
                  />
                )}
                <LegendKey
                  dotColor="info.400"
                  text={`Scheduled to vest: ${yetToVestShares.toLocaleString()}`}
                />
                {ownedSharesCount > 0 && (
                  <LegendKey
                    dotColor="purple.500"
                    text={`Exercised and Owned: ${ownedSharesCount.toLocaleString()}`}
                  />
                )}
              </>
            )}
            {sharesToAllocate > 0 && (
              <LegendKey
                dotColor="neutral.200"
                text={`Unscheduled: ${sharesToAllocate.toLocaleString()}`}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};
